import React, { useEffect, useRef } from 'react';
import { getShaderPreset, hexToVec3 } from '../lib/shaderPresets';

interface ShaderBackgroundProps {
  preset: string;
  primaryColor?: string;
  secondaryColor?: string;
  className?: string;
}

const ShaderBackground: React.FC<ShaderBackgroundProps> = ({
  preset,
  primaryColor = '#f97316',
  secondaryColor = '#3b82f6',
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const mountedRef = useRef(false);
  const colorsRef = useRef<{ p1: [number, number, number]; p2: [number, number, number] }>({
    p1: hexToVec3(primaryColor),
    p2: hexToVec3(secondaryColor),
  });

  const shaderData = getShaderPreset(preset);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !shaderData) return;

    const gl = canvas.getContext('webgl', { alpha: false });
    if (!gl) return;
    glRef.current = gl;

    const vertSrc = `
      attribute vec2 aPos;
      void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
    `;
    const fragSrc = `
      precision mediump float;
      uniform float iTime;
      uniform vec2 iResolution;
      ${shaderData.fs}
      void main() {
        vec4 col;
        mainImage(col, gl_FragCoord.xy);

        // Ensure primary color dominates: blend shader result over a primary-color base.
        // Vignette gradient from primary (center) to primary*0.3 (edges).
        vec2 uv = gl_FragCoord.xy / iResolution.xy;
        float vig = 1.0 - smoothstep(0.0, 0.9, length(uv - 0.5) * 1.4);
        vec3 base = mix(uPrimaryColor * 0.35, uPrimaryColor * 0.9, vig);

        // Additive overlay of shader pattern, so the brand color stays dominant
        // but the shader motion/texture is clearly visible.
        vec3 finalColor = base + col.rgb * 0.85;
        gl_FragColor = vec4(finalColor, 1.0);
      }
    `;

    const compileShader = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.warn('Shader compile error:', gl.getShaderInfoLog(s));
      }
      return s;
    };

    const vert = compileShader(gl.VERTEX_SHADER, vertSrc);
    const frag = compileShader(gl.FRAGMENT_SHADER, fragSrc);
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vert);
    gl.attachShader(prog, frag);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn('Shader link error:', gl.getProgramInfoLog(prog));
      return;
    }
    gl.useProgram(prog);
    programRef.current = prog;

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const pos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

    startTimeRef.current = Date.now();
    mountedRef.current = true;

    const syncSize = () => {
      const w = canvas.clientWidth || canvas.parentElement?.clientWidth || 300;
      const h = canvas.clientHeight || canvas.parentElement?.clientHeight || 200;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };

    const ro = new ResizeObserver(() => syncSize());
    ro.observe(canvas);
    // Also observe parent in case canvas itself has no intrinsic size
    if (canvas.parentElement) ro.observe(canvas.parentElement);
    syncSize();

    const render = () => {
      if (!mountedRef.current) return;
      syncSize();
      const t = (Date.now() - startTimeRef.current) / 1000;
      gl.uniform1f(gl.getUniformLocation(prog, 'iTime'), t);
      gl.uniform2f(gl.getUniformLocation(prog, 'iResolution'), canvas.width, canvas.height);
      gl.uniform3fv(gl.getUniformLocation(prog, 'uPrimaryColor'), colorsRef.current.p1);
      gl.uniform3fv(gl.getUniformLocation(prog, 'uSecondaryColor'), colorsRef.current.p2);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    return () => {
      mountedRef.current = false;
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [preset, shaderData]);

  // Live color updates — update refs so the render loop picks them up each frame
  useEffect(() => {
    colorsRef.current = {
      p1: hexToVec3(primaryColor),
      p2: hexToVec3(secondaryColor),
    };
  }, [primaryColor, secondaryColor]);

  if (!shaderData) return null;

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ display: 'block', width: '100%', height: '100%', position: 'absolute', inset: 0 }}
    />
  );
};

export default ShaderBackground;
